/**
 * AI Workflow Dashboard Component
 *
 * Main dashboard for AI workflow decomposition and execution.
 * Integrates:
 * - Task input form
 * - Workflow visualization
 * - Real-time execution monitoring
 * - Workflow history
 * - Cost analytics
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  GitBranch,
  Clock,
  DollarSign,
  AlertTriangle,
  ChevronLeft,
  Plus,
  List,
  BookOpen,
  Filter,
  ChevronDown,
  ChevronRight,
  Loader,
  RefreshCw,
  Tag,
  FolderOpen,
  Download,
  File
} from 'lucide-react';

import AIWorkflowTaskForm from './AIWorkflowTaskForm';
import AIWorkflowVisualization from './AIWorkflowVisualization';
import AIWorkflowTemplateBrowser from './AIWorkflowTemplateBrowser';
import WorkflowOutputViewer from './WorkflowOutputViewer';
import AIWorkflowCloneModal from './AIWorkflowCloneModal';
import { fetchWorkflowHistory } from '../api/workflowsApi';
import { listWorkflowFiles, downloadFile } from '../api/filesApi';
import { getWorkflowIdentifier } from '../utils/workflowIdentifier';
import { useAIWorkflowDecompose } from '../hooks/useAIWorkflow';

/**
 * Workflow summary card
 */
function WorkflowSummaryCard({ metadata }) {
  if (!metadata) return null;

  const {
    total_nodes,
    estimated_cost,
    estimated_duration_seconds,
    risk_analysis
  } = metadata;

  const durationMinutes = Math.ceil(estimated_duration_seconds / 60);

  const riskColorMap = {
    low: 'text-green-600 bg-green-50 border-green-200',
    medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    high: 'text-orange-600 bg-orange-50 border-orange-200',
    critical: 'text-red-600 bg-red-50 border-red-200'
  };

  const riskColor = riskColorMap[risk_analysis?.risk_level] || riskColorMap.low;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
        <div className="flex items-center gap-2 text-gray-600 mb-2">
          <GitBranch size={18} />
          <span className="text-sm font-medium">Total Tasks</span>
        </div>
        <div className="text-2xl font-bold text-gray-900">{total_nodes}</div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
        <div className="flex items-center gap-2 text-gray-600 mb-2">
          <DollarSign size={18} />
          <span className="text-sm font-medium">Estimated Cost</span>
        </div>
        <div className="text-2xl font-bold text-gray-900">
          ${estimated_cost?.toFixed(4) || '0.00'}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
        <div className="flex items-center gap-2 text-gray-600 mb-2">
          <Clock size={18} />
          <span className="text-sm font-medium">Est. Duration</span>
        </div>
        <div className="text-2xl font-bold text-gray-900">
          ~{durationMinutes} min
        </div>
      </div>

      <div className={`rounded-lg shadow p-4 border ${riskColor}`}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={18} />
          <span className="text-sm font-medium">Risk Level</span>
        </div>
        <div className="text-2xl font-bold capitalize">
          {risk_analysis?.risk_level || 'Low'}
        </div>
        {risk_analysis?.high_risk_nodes > 0 && (
          <div className="text-xs mt-1">
            {risk_analysis.high_risk_nodes} high-risk task(s)
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Workflow history component with filtering, sorting, and pagination
 */
function WorkflowHistory({ onSelectWorkflow, onCloneWorkflow }) {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);

  // Filters and pagination
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedWorkflow, setExpandedWorkflow] = useState(null);
  const [viewingOutput, setViewingOutput] = useState(null); // Workflow ID whose output is being viewed
  const [viewingFiles, setViewingFiles] = useState(null); // Workflow ID whose files are being viewed
  const [workflowFiles, setWorkflowFiles] = useState({}); // { workflowId: { files, loading, error } }
  const pageSize = 10;

  // Clone workflow state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [workflowToClone, setWorkflowToClone] = useState(null);

  // Fetch workflow history
  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchWorkflowHistory({
        limit: pageSize,
        offset: currentPage * pageSize,
        status: statusFilter || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        include_cost_breakdown: true,
        cost_breakdown_detail: 'summary',
        summarize_descriptions: false
      });

      setWorkflows(data.workflows);
      setTotalCount(data.total_count);
      setSummary(data.summary);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load workflow history:', err);
      setError('Failed to load workflow history. Please try again.');
      setLoading(false);
    }
  }, [pageSize, currentPage, statusFilter, sortBy, sortOrder]);

  // Load on mount and when filters change
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Status badge color
  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      executing: 'bg-blue-100 text-blue-800 border-blue-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      payment_required: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      paused: 'bg-orange-100 text-orange-800 border-orange-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
      ready: 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return colors[status] || colors.draft;
  };

  // Format cost
  const formatCost = (cost) => {
    if (cost === null || cost === undefined) return 'N/A';
    return `$${cost.toFixed(4)}`;
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Toggle expanded workflow
  const toggleExpanded = (workflowId) => {
    setExpandedWorkflow(expandedWorkflow === workflowId ? null : workflowId);
  };

  // Toggle files view for a workflow
  const toggleFiles = async (workflowId) => {
    if (viewingFiles === workflowId) {
      setViewingFiles(null);
      return;
    }
    setViewingFiles(workflowId);
    // Fetch files if not already cached
    if (!workflowFiles[workflowId]) {
      setWorkflowFiles(prev => ({ ...prev, [workflowId]: { files: [], loading: true, error: null } }));
      try {
        const data = await listWorkflowFiles(workflowId);
        setWorkflowFiles(prev => ({ ...prev, [workflowId]: { files: data.files || [], loading: false, error: null } }));
      } catch (err) {
        setWorkflowFiles(prev => ({ ...prev, [workflowId]: { files: [], loading: false, error: err.message } }));
      }
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && workflows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Loader size={48} className="mx-auto text-blue-600 mb-4 animate-spin" />
        <p className="text-gray-600">Loading workflow history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading History</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={loadHistory}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <DollarSign size={18} />
              <span className="text-sm font-medium">Total Cost</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              ${summary.total_cost?.toFixed(4) || '0.00'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Avg: ${summary.average_cost?.toFixed(4) || '0.00'}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Clock size={18} />
              <span className="text-sm font-medium">Total Duration</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatDuration(summary.total_duration_seconds)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Avg: {formatDuration(summary.average_duration_seconds)}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <List size={18} />
              <span className="text-sm font-medium">Workflows</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
            <div className="text-xs text-gray-500 mt-1">
              {Object.entries(summary.status_distribution || {}).map(([status, count]) => (
                <span key={status} className="mr-2">
                  {status}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters and sorting */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(0);
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="executing">Executing</option>
            <option value="draft">Draft</option>
            <option value="payment_required">Payment Required</option>
            <option value="paused">Paused</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="created_at">Created Date</option>
            <option value="completed_at">Completed Date</option>
            <option value="actual_cost">Cost</option>
            <option value="actual_duration_seconds">Duration</option>
          </select>

          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>

          <button
            onClick={loadHistory}
            disabled={loading}
            className="ml-auto px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Workflow list */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {workflows.length === 0 ? (
          <div className="p-8 text-center">
            <List size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Workflows Found</h3>
            <p className="text-gray-600">
              {statusFilter ? 'Try changing your filters' : 'Create your first workflow to get started'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {workflows.map((workflow) => {
              const isExpanded = expandedWorkflow === workflow.workflow_id;
              const workflowIdentifier = getWorkflowIdentifier({ id: workflow.workflow_id });

              return (
                <div key={workflow.workflow_id} className="hover:bg-gray-50 transition-colors">
                  <div className="p-4">
                    <div className="flex items-start gap-4">
                      <button
                        onClick={() => toggleExpanded(workflow.workflow_id)}
                        className="flex-shrink-0 mt-1"
                      >
                        {isExpanded ? (
                          <ChevronDown size={20} className="text-gray-600" />
                        ) : (
                          <ChevronRight size={20} className="text-gray-600" />
                        )}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Tag size={14} className="text-blue-600 flex-shrink-0" />
                              <span className="text-xs font-mono text-blue-700 font-medium">
                                {workflowIdentifier}
                              </span>
                            </div>
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {workflow.task_description}
                            </h4>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(workflow.status)}`}>
                                {workflow.status}
                              </span>
                              {workflow.domain && (
                                <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200">
                                  {workflow.domain}
                                </span>
                              )}
                              {workflow.role && (
                                <span className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded border border-purple-200">
                                  {workflow.role}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="text-right flex-shrink-0">
                            <div className="text-sm font-semibold text-gray-900">
                              {formatCost(workflow.actual_cost || workflow.estimated_cost)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {workflow.cost_variance ? (
                                <span className={workflow.cost_variance > 0 ? 'text-red-600' : 'text-green-600'}>
                                  {workflow.cost_variance > 0 ? '+' : ''}
                                  {workflow.cost_variance_percentage?.toFixed(1)}%
                                </span>
                              ) : (
                                'estimated'
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600">
                          <div>
                            <span className="font-medium">Created:</span> {formatDate(workflow.created_at)}
                          </div>
                          <div>
                            <span className="font-medium">Duration:</span> {formatDuration(workflow.actual_duration_seconds)}
                          </div>
                          <div>
                            <span className="font-medium">Nodes:</span> {workflow.total_nodes}
                          </div>
                          <div>
                            <span className="font-medium">Depth:</span> {workflow.max_depth}
                          </div>
                        </div>

                        {isExpanded && workflow.cost_breakdown && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <h5 className="text-sm font-semibold text-gray-900 mb-3">Cost Breakdown</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <div className="text-xs font-medium text-gray-700 mb-2">By Node Type</div>
                                <div className="space-y-1">
                                  {Object.entries(workflow.cost_breakdown.breakdown_by_node_type || {}).map(
                                    ([type, cost]) => (
                                      <div key={type} className="flex justify-between text-xs">
                                        <span className="text-gray-600">{type}:</span>
                                        <span className="font-medium">${cost.toFixed(4)}</span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>

                              <div>
                                <div className="text-xs font-medium text-gray-700 mb-2">By Status</div>
                                <div className="space-y-1">
                                  {Object.entries(workflow.cost_breakdown.breakdown_by_status || {}).map(
                                    ([status, cost]) => (
                                      <div key={status} className="flex justify-between text-xs">
                                        <span className="text-gray-600">{status}:</span>
                                        <span className="font-medium">${cost.toFixed(4)}</span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>

                            {workflow.cost_breakdown.top_cost_nodes &&
                              workflow.cost_breakdown.top_cost_nodes.length > 0 && (
                                <div className="mt-4">
                                  <div className="text-xs font-medium text-gray-700 mb-2">Top Cost Nodes</div>
                                  <div className="space-y-1">
                                    {workflow.cost_breakdown.top_cost_nodes.map((node, idx) => (
                                      <div key={idx} className="flex justify-between text-xs">
                                        <span className="text-gray-600 truncate flex-1">{node.display_name}:</span>
                                        <span className="font-medium ml-2">${node.cost.toFixed(4)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                            <div className="mt-4 flex gap-2 flex-wrap">
                              {onSelectWorkflow && (
                                <button
                                  onClick={() => onSelectWorkflow(workflow)}
                                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                                >
                                  View Details
                                </button>
                              )}
                              {workflow.status === 'completed' && (
                                <button
                                  onClick={() => setViewingOutput(viewingOutput === workflow.workflow_id ? null : workflow.workflow_id)}
                                  className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                                >
                                  {viewingOutput === workflow.workflow_id ? 'Hide Output' : 'View Output'}
                                </button>
                              )}
                              <button
                                onClick={() => toggleFiles(workflow.workflow_id)}
                                className="px-3 py-1.5 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 transition-colors flex items-center gap-1"
                              >
                                <FolderOpen className="w-3 h-3" />
                                {viewingFiles === workflow.workflow_id ? 'Hide Files' : 'Show Files'}
                              </button>
                              <button
                                onClick={() => {
                                  setWorkflowToClone(workflow);
                                  setShowCloneModal(true);
                                }}
                                className="px-3 py-1.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 transition-colors flex items-center gap-1"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Clone & Edit
                              </button>
                            </div>

                            {/* Output viewer - shown when viewing output */}
                            {viewingOutput === workflow.workflow_id && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <WorkflowOutputViewer workflowId={workflow.workflow_id} />
                              </div>
                            )}

                            {/* Files viewer - shown when viewing files */}
                            {viewingFiles === workflow.workflow_id && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h5 className="text-sm font-semibold text-gray-900 mb-3">Workflow Files</h5>
                                {workflowFiles[workflow.workflow_id]?.loading ? (
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Loader size={14} className="animate-spin" />
                                    Loading files...
                                  </div>
                                ) : workflowFiles[workflow.workflow_id]?.error ? (
                                  <div className="text-sm text-red-600">
                                    {workflowFiles[workflow.workflow_id].error}
                                  </div>
                                ) : (() => {
                                  const files = workflowFiles[workflow.workflow_id]?.files || [];
                                  const outputFiles = files.filter(f => f.file_type === 'output');
                                  const inputFiles = files.filter(f => f.file_type !== 'output');
                                  return files.length === 0 ? (
                                    <div className="text-sm text-gray-400 italic">No files associated with this workflow</div>
                                  ) : (
                                    <div className="space-y-3">
                                      {outputFiles.length > 0 && (
                                        <div>
                                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Output Files</div>
                                          <div className="space-y-1">
                                            {outputFiles.map(file => (
                                              <div key={file.file_id} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                                                <div className="flex items-center gap-2 min-w-0">
                                                  <File size={14} className="text-gray-400 shrink-0" />
                                                  <span className="text-sm text-gray-700 truncate">{file.filename}</span>
                                                  <span className="text-xs text-gray-400 shrink-0">{file.mime_type}</span>
                                                </div>
                                                <button
                                                  onClick={() => downloadFile(file.file_id, file.filename)}
                                                  className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition shrink-0 ml-2"
                                                >
                                                  <Download size={12} />
                                                  Download
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {inputFiles.length > 0 && (
                                        <div>
                                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Input Files</div>
                                          <div className="space-y-1">
                                            {inputFiles.map(file => (
                                              <div key={file.file_id} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                                                <div className="flex items-center gap-2 min-w-0">
                                                  <File size={14} className="text-gray-400 shrink-0" />
                                                  <span className="text-sm text-gray-700 truncate">{file.filename}</span>
                                                  <span className="text-xs text-gray-400 shrink-0">{file.mime_type}</span>
                                                </div>
                                                <button
                                                  onClick={() => downloadFile(file.file_id, file.filename)}
                                                  className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition shrink-0 ml-2"
                                                >
                                                  <Download size={12} />
                                                  Download
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  const pageNum = idx;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1.5 rounded-lg text-sm ${currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Workflow Modal */}
      <AIWorkflowCloneModal
        show={showCloneModal}
        onClose={() => {
          setShowCloneModal(false);
          setWorkflowToClone(null);
        }}
        onClone={(cloneData) => {
          if (onCloneWorkflow) {
            onCloneWorkflow(workflowToClone, cloneData);
          }
          setShowCloneModal(false);
          setWorkflowToClone(null);
        }}
        originalWorkflow={workflowToClone}
        cloning={false}
      />
    </div>
  );
}

/**
 * Main dashboard component
 */
export default function AIWorkflowDashboard({
  viewMode = 'form',
  initialTemplate,
  initialWorkflow,
  currentWorkflow: propsCurrentWorkflow,
  currentGraph: propsCurrentGraph,
  currentMetadata: propsCurrentMetadata,
  onViewModeChange,
  onWorkflowDataChange
}) {
  // Use props if provided, otherwise use local state (for backwards compatibility)
  const currentWorkflow = propsCurrentWorkflow !== undefined ? propsCurrentWorkflow : null;
  const currentGraph = propsCurrentGraph !== undefined ? propsCurrentGraph : null;
  const currentMetadata = propsCurrentMetadata !== undefined ? propsCurrentMetadata : null;

  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate || null);
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);
  const [workflowError, setWorkflowError] = useState(null);

  const handleWorkflowCreated = (data) => {
    // Update workflow data in parent component
    if (onWorkflowDataChange) {
      onWorkflowDataChange(data.workflow, data.graph, data.metadata);
    }
    // Request mode change to visualization
    if (onViewModeChange) {
      onViewModeChange('visualization');
    }
  };

  const handleBackToForm = () => {
    // Request mode change to form
    if (onViewModeChange) {
      onViewModeChange('form');
    }
    // Optionally reset current workflow
    // setCurrentWorkflow(null);
    // setCurrentGraph(null);
    // setCurrentMetadata(null);
  };

  const handleTemplateSelected = (template) => {
    setSelectedTemplate(template);
    // Request mode change to form
    if (onViewModeChange) {
      onViewModeChange('form');
    }
  };

  const handleSelectWorkflow = async (workflow) => {
    setLoadingWorkflow(true);
    setWorkflowError(null);

    try {
      // Import fetchWorkflowById directly
      const { fetchWorkflowById } = await import('../api/workflowsApi');

      // Fetch full workflow details including graph
      const data = await fetchWorkflowById(workflow.workflow_id);

      const metadata = {
        total_nodes: data.total_nodes,
        estimated_cost: data.estimated_cost,
        estimated_duration_seconds: data.estimated_duration_seconds,
        risk_analysis: data.risk_analysis
      };

      // Update workflow data in parent component
      if (onWorkflowDataChange) {
        onWorkflowDataChange(data.workflow, data.graph, metadata);
      }

      // Request mode change to visualization
      if (onViewModeChange) {
        onViewModeChange('visualization');
      }
    } catch (error) {
      console.error('Failed to load workflow details:', error);
      setWorkflowError('Failed to load workflow details. Please try again.');
    } finally {
      setLoadingWorkflow(false);
    }
  };

  const handleCloneWorkflow = async (originalWorkflow, cloneData) => {
    setLoadingWorkflow(true);
    setWorkflowError(null);

    try {
      // Import axios
      const axios = (await import('axios')).default;

      // Call clone API to duplicate workflow structure with fresh execution state
      const response = await axios.post(
        `/api/v1/workflows/${originalWorkflow.workflow_id}/clone`,
        {
          task_description: cloneData.task_description,
          domain: cloneData.domain,
          company: cloneData.company,
          role: cloneData.role,
          constraints: cloneData.constraints
        }
      );

      const data = response.data;

      if (data) {
        const metadata = {
          total_nodes: data.total_nodes,
          estimated_cost: data.estimated_cost,
          estimated_duration_seconds: data.estimated_duration_seconds,
          risk_analysis: data.risk_analysis
        };

        // Update workflow data in parent component
        if (onWorkflowDataChange) {
          onWorkflowDataChange(data.workflow, data.graph, metadata);
        }

        // Request mode change to visualization to show the cloned workflow
        if (onViewModeChange) {
          onViewModeChange('visualization');
        }
      }
    } catch (error) {
      console.error('Failed to clone workflow:', error);
      setWorkflowError('Failed to clone workflow. Please try again.');
    } finally {
      setLoadingWorkflow(false);
    }
  };

  // Update when workflow is created from canvas
  React.useEffect(() => {
    if (initialWorkflow && onWorkflowDataChange) {
      onWorkflowDataChange(
        initialWorkflow.workflow,
        initialWorkflow.graph,
        initialWorkflow.metadata
      );
    }
  }, [initialWorkflow, onWorkflowDataChange]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              AI Workflow Orchestrator
            </h1>
            <p className="text-gray-600">
              Break down complex tasks into intelligent, executable workflows
            </p>
          </div>

          {currentWorkflow && viewMode === 'visualization' && (
            <button
              onClick={handleBackToForm}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft size={18} />
              <span>Back to Form</span>
            </button>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="max-w-7xl mx-auto">
        {/* Loading state */}
        {loadingWorkflow && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Loader size={48} className="mx-auto text-blue-600 mb-4 animate-spin" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Workflow...</h3>
            <p className="text-gray-600">Fetching workflow details and graph data</p>
          </div>
        )}

        {/* Error state */}
        {workflowError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{workflowError}</p>
            <button
              onClick={handleBackToForm}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Create New
            </button>
          </div>
        )}

        {!loadingWorkflow && !workflowError && viewMode === 'form' && (
          <AIWorkflowTaskForm
            onSuccess={handleWorkflowCreated}
            initialTemplate={selectedTemplate}
          />
        )}

        {!loadingWorkflow && !workflowError && viewMode === 'visualization' && currentWorkflow && currentGraph && (
          <div>
            {/* Workflow summary */}
            <WorkflowSummaryCard metadata={currentMetadata} />

            {/* Risk warnings */}
            {currentMetadata?.risk_analysis && currentMetadata.risk_analysis.risk_level !== 'low' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-yellow-900 mb-1">
                      {currentMetadata.risk_analysis.risk_level === 'critical'
                        ? 'Critical Risk Detected'
                        : currentMetadata.risk_analysis.risk_level === 'high'
                          ? 'High Risk Workflow'
                          : 'Medium Risk Workflow'}
                    </div>
                    <div className="text-sm text-yellow-800">
                      This workflow contains {currentMetadata.risk_analysis.high_risk_nodes} high-risk
                      task(s) that will require human approval. Review carefully before execution.
                    </div>
                    {currentMetadata.risk_analysis.risk_factors &&
                      currentMetadata.risk_analysis.risk_factors.length > 0 && (
                        <ul className="mt-2 text-sm text-yellow-800 list-disc list-inside">
                          {currentMetadata.risk_analysis.risk_factors.slice(0, 3).map((factor, i) => (
                            <li key={i}>{factor}</li>
                          ))}
                        </ul>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Workflow info card */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex items-start gap-3">
                <FileText size={20} className="text-blue-600 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 mb-1">
                    Workflow: {currentWorkflow.task_description}
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                    {currentWorkflow.domain && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        {currentWorkflow.domain}
                      </span>
                    )}
                    {currentWorkflow.role && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                        {currentWorkflow.role}
                      </span>
                    )}
                    {currentWorkflow.company && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                        {currentWorkflow.company}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Visualization */}
            <div className="h-[calc(100vh-200px)] min-h-[800px]">
              <AIWorkflowVisualization
                key={currentWorkflow.id || currentWorkflow.workflow_id}
                workflowId={currentWorkflow.id || currentWorkflow.workflow_id}
                initialGraph={currentGraph}
                initialWorkflow={currentWorkflow}
              />
            </div>
          </div>
        )}

        {!loadingWorkflow && !workflowError && viewMode === 'visualization' && !currentWorkflow && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <GitBranch size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Workflow Selected</h3>
            <p className="text-gray-600 mb-4">
              Create a new workflow to see the visualization
            </p>
            <button
              onClick={handleBackToForm}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Workflow
            </button>
          </div>
        )}

        {!loadingWorkflow && !workflowError && viewMode === 'history' && (
          <WorkflowHistory
            onSelectWorkflow={handleSelectWorkflow}
            onCloneWorkflow={handleCloneWorkflow}
          />
        )}
      </div>
    </div>
  );
}
